import { useMemo, useState } from 'react';
import { Button, cn } from '@jeraisonne/ui';
import { GitBranch, Loader2, AlertCircle, Sparkles, Layers, ChevronDown } from 'lucide-react';
import { useAdminChapters } from '@/lib/admin/queries';
import { useCreateV2Exercise } from '@/lib/exercise-v2/api';
import { useTypesDeProbleme, useBuildDag, useDag } from '@/lib/exercise-dag/api';
import {
  isDagPayload,
  type DagPayload,
  type DagNoeud,
  type DagSubnodeTrigger,
} from '@/lib/exercise-dag/types';

const NIVEAUX = [
  '6e',
  '5e',
  '4e',
  '3e',
  '2nde',
  '1ere',
  'Tle',
  'Tle_complementaires',
  'Tle_expertes',
] as const;

/** Rang scolaire croissant des niveaux du catalogue (filtrage cumulatif des types). */
const NIVEAU_RANK: Record<string, number> = Object.fromEntries(NIVEAUX.map((n, i) => [n, i]));

/**
 * Niveau du catalogue → valeur `school_level` des chapitres (qui utilise `1re`/`tle`).
 * Tle_complementaires/expertes n'ont pas de niveau de chapitre dédié → `tle`.
 */
const NIVEAU_TO_SCHOOL: Record<string, string> = {
  '6e': '6e',
  '5e': '5e',
  '4e': '4e',
  '3e': '3e',
  '2nde': '2nde',
  '1ere': '1re',
  Tle: 'tle',
  Tle_complementaires: 'tle',
  Tle_expertes: 'tle',
};

/**
 * Route admin `/tutor/admin/dag` — authoring « solve-backward ».
 * L'admin colle un énoncé + corrigé, choisit un type de problème du catalogue,
 * et lance la construction du DAG d'inférence (LLM + fermeture catalogue).
 * Le DAG est affiché en LECTURE SEULE (l'éditeur viendra dans une itération
 * ultérieure).
 */
export function TutorAdminDagPage() {
  const chapters = useAdminChapters();
  const types = useTypesDeProbleme();
  const createExercise = useCreateV2Exercise();
  const build = useBuildDag();

  const [niveau, setNiveau] = useState<string>('');
  const [chapterId, setChapterId] = useState('');
  const [title, setTitle] = useState('');
  const [typeId, setTypeId] = useState('');
  const [enonce, setEnonce] = useState('');
  const [corrige, setCorrige] = useState('');
  const [exerciseId, setExerciseId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Chapitres du niveau choisi (school_level) ; types apparaissant au plus tard à
  // ce niveau (un exercice de 3e peut réinvestir un type de 4e).
  const schoolLevel = niveau ? NIVEAU_TO_SCHOOL[niveau] : null;
  const filteredChapters = useMemo(
    () => (chapters.data ?? []).filter((c) => !schoolLevel || c.level === schoolLevel),
    [chapters.data, schoolLevel],
  );
  const filteredTypes = useMemo(
    () =>
      (types.data ?? []).filter(
        (t) => niveau && (NIVEAU_RANK[t.niveau_apparition] ?? 0) <= (NIVEAU_RANK[niveau] ?? 0),
      ),
    [types.data, niveau],
  );

  /** Changer de niveau réinitialise chapitre + type (filtres dépendants). */
  function handleNiveauChange(value: string) {
    setNiveau(value);
    setChapterId('');
    setTypeId('');
  }

  const dag = useDag(exerciseId);
  const busy = createExercise.isPending || build.isPending;
  const canSubmit =
    !busy &&
    !!niveau &&
    !!chapterId &&
    !!typeId &&
    enonce.trim().length >= 10 &&
    corrige.trim().length >= 10;

  async function handleBuild() {
    setError(null);
    try {
      // Le DAG se rattache à un exercice : on en crée un brouillon (ou on réutilise
      // celui de la session courante en mode reconstruct).
      const exId =
        exerciseId ??
        (await createExercise.mutateAsync({
          chapter_id: chapterId,
          raw_statement: enonce,
          title: title.trim() || 'Exercice DAG',
        }));
      setExerciseId(exId);
      await build.mutateAsync({
        exercise_id: exId,
        type_id: typeId,
        niveau,
        enonce,
        corrige,
        mode: 'reconstruct',
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 pb-[max(env(safe-area-inset-bottom),1.5rem)] sm:py-8">
      <header className="mb-5 flex items-center gap-2.5">
        <div className="bg-primary-50 flex h-10 w-10 items-center justify-center rounded-xl">
          <GitBranch className="text-primary-500 h-5 w-5" />
        </div>
        <div>
          <h1 className="font-display text-ink-900 text-xl font-extrabold leading-tight">
            DAG solve-backward
          </h1>
          <p className="text-ink-500 text-xs">
            Énoncé + corrigé → DAG d&apos;inférence validé sur le catalogue (lecture seule)
          </p>
        </div>
      </header>

      {types.data && types.data.length === 0 && (
        <div className="bg-accent-50 border-accent-200 text-accent-700 mb-4 flex items-start gap-2 rounded-xl border px-4 py-3 text-sm">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Catalogue vide : applique les seeds <code>0146/0147</code> (types de problème) avant de
            construire un DAG.
          </span>
        </div>
      )}

      {/* Formulaire */}
      <div className="border-ink-200 space-y-4 rounded-2xl border bg-white p-4 sm:p-5">
        <Field label="Niveau">
          <select
            value={niveau}
            onChange={(e) => handleNiveauChange(e.target.value)}
            className="border-ink-200 text-ink-900 min-h-[2.75rem] w-full rounded-xl border bg-white px-3 text-sm"
          >
            <option value="">— Choisir un niveau —</option>
            {NIVEAUX.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Chapitre">
          <select
            value={chapterId}
            onChange={(e) => setChapterId(e.target.value)}
            disabled={!niveau}
            className="border-ink-200 text-ink-900 min-h-[2.75rem] w-full rounded-xl border bg-white px-3 text-sm disabled:opacity-50"
          >
            <option value="">
              {!niveau
                ? '— Choisis d’abord un niveau —'
                : filteredChapters.length === 0
                  ? `— Aucun chapitre en ${niveau} —`
                  : '— Choisir un chapitre —'}
            </option>
            {filteredChapters.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title} · {c.subject}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Type de problème (catalogue)">
          <select
            value={typeId}
            onChange={(e) => setTypeId(e.target.value)}
            disabled={!niveau || filteredTypes.length === 0}
            className="border-ink-200 text-ink-900 min-h-[2.75rem] w-full rounded-xl border bg-white px-3 text-sm disabled:opacity-50"
          >
            <option value="">
              {!niveau ? '— Choisis d’abord un niveau —' : '— Choisir un type —'}
            </option>
            {filteredTypes.map((t) => (
              <option key={t.id} value={t.id}>
                {t.id}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Titre (optionnel)">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Exercice DAG"
            className="border-ink-200 text-ink-900 min-h-[2.75rem] w-full rounded-xl border bg-white px-3 text-sm"
          />
        </Field>

        <Field label="Énoncé">
          <textarea
            value={enonce}
            onChange={(e) => setEnonce(e.target.value)}
            rows={4}
            placeholder="ABC est un triangle tel que AB = 6 cm, AC = 8 cm, BC = 10 cm…"
            className="border-ink-200 text-ink-900 w-full rounded-xl border bg-white px-3 py-2 text-sm"
          />
        </Field>

        <Field label="Corrigé (source de vérité — sera structuré, pas recalculé)">
          <textarea
            value={corrige}
            onChange={(e) => setCorrige(e.target.value)}
            rows={6}
            placeholder="a) D'après la réciproque de Pythagore… b) Aire = … c) On en déduit AH = …"
            className="border-ink-200 text-ink-900 w-full rounded-xl border bg-white px-3 py-2 text-sm"
          />
        </Field>

        {error && (
          <div className="bg-error-50 border-error-100 text-error-700 flex items-start gap-2 rounded-xl border px-3 py-2 text-sm">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <Button onClick={handleBuild} disabled={!canSubmit} fullWidth>
          {busy ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Construction…
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2">
              <Sparkles className="h-4 w-4" /> Construire le DAG
            </span>
          )}
        </Button>
      </div>

      {/* Résultat */}
      {exerciseId && isDagPayload(dag.data) && <DagViewer dag={dag.data} />}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-ink-600 mb-1 block text-xs font-semibold uppercase tracking-wide">
        {label}
      </span>
      {children}
    </label>
  );
}

// ─── Viewer (lecture seule) ──────────────────────────────────────────────────

const TRIGGER_LABEL: Record<DagSubnodeTrigger, string> = {
  on_error: 'à l’erreur',
  on_request: 'sur demande',
  always: 'toujours',
};

export function DagViewer({ dag }: { dag: DagPayload }) {
  const indiceById = useMemo(() => new Map(dag.indices.map((i) => [i.id, i])), [dag.indices]);
  const noeudById = useMemo(() => new Map(dag.noeuds.map((n) => [n.id, n])), [dag.noeuds]);

  // Nœuds PRINCIPAUX (ordre topologique) ; les sous-nœuds vivent hors du DAG
  // principal et sont regroupés par le principal qu'ils décomposent (chemin développé).
  const mainNoeuds = useMemo(
    () => dag.noeuds.filter((n) => !n.is_sub).sort((a, b) => a.ordre - b.ordre),
    [dag.noeuds],
  );
  const subnodesByMain = useMemo(() => {
    const map = new Map<string, DagNoeud[]>();
    for (const n of dag.noeuds) {
      if (!n.is_sub || !n.subnode_of) continue;
      const arr = map.get(n.subnode_of) ?? [];
      arr.push(n);
      map.set(n.subnode_of, arr);
    }
    for (const arr of map.values()) arr.sort((a, b) => a.ordre - b.ordre);
    return map;
  }, [dag.noeuds]);
  const subCount = dag.noeuds.length - mainNoeuds.length;

  return (
    <section className="mt-6 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="font-display text-ink-900 text-base font-bold">DAG construit</h2>
        <span className="bg-ink-100 text-ink-600 rounded-full px-2 py-0.5 text-[11px] font-semibold">
          {dag.dag.type_id}
        </span>
        <span className="bg-primary-50 text-primary-600 rounded-full px-2 py-0.5 text-[11px] font-semibold">
          {dag.dag.niveau}
        </span>
        <span className="text-ink-400 text-[11px]">
          {mainNoeuds.length} nœuds · {dag.indices.length} indices
          {subCount > 0 ? ` · ${subCount} sous-nœud${subCount > 1 ? 's' : ''}` : ''}
        </span>
      </div>

      {dag.indices_non_consommes.length > 0 && (
        <div className="bg-accent-50 border-accent-200 text-accent-700 rounded-xl border px-3 py-2 text-sm">
          <p className="mb-1 font-semibold">
            {dag.indices_non_consommes.length} indice(s) non consommé(s) = distracteurs
            d&apos;énoncé
          </p>
          <p className="text-accent-600 text-xs">
            {dag.indices_non_consommes
              .map((i) => i.valeur ?? `[${i.span_debut}-${i.span_fin}]`)
              .join(' · ')}
          </p>
        </div>
      )}

      {mainNoeuds.map((n, idx) => (
        <NodeCard
          key={n.id}
          node={n}
          index={idx}
          dag={dag}
          indiceById={indiceById}
          noeudById={noeudById}
          subnodes={subnodesByMain.get(n.id) ?? []}
        />
      ))}
    </section>
  );
}

/** Liste de choix QCM (réutilisée par les nœuds principaux et les sous-nœuds). */
function ChoixList({ choix }: { choix: DagPayload['choix'] }) {
  if (choix.length === 0) return null;
  return (
    <ul className="space-y-1 pt-1">
      {choix.map((c) => (
        <li
          key={c.id}
          className={cn(
            'flex items-start gap-2 rounded-lg border px-2.5 py-1.5 text-sm',
            c.correct
              ? 'border-xp-200 bg-xp-50 text-xp-700'
              : 'border-error-200 bg-error-50 text-error-700',
          )}
        >
          <span className="mt-0.5 shrink-0 text-xs font-bold">{c.correct ? '✓' : '✗'}</span>
          <span className="flex-1">
            {c.label}
            {!c.correct && c.misconception_id && (
              <span className="text-error-500 ml-1 text-[11px]">({c.misconception_id})</span>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

function NodeCard({
  node,
  index,
  dag,
  indiceById,
  noeudById,
  subnodes,
}: {
  node: DagNoeud;
  index: number;
  dag: DagPayload;
  indiceById: Map<string, DagPayload['indices'][number]>;
  noeudById: Map<string, DagNoeud>;
  subnodes: DagNoeud[];
}) {
  const [expanded, setExpanded] = useState(false);
  const parents = dag.aretes.filter((a) => a.noeud_id === node.id);
  const choix = dag.choix.filter((c) => c.noeud_id === node.id).sort((a, b) => a.ordre - b.ordre);
  const hasSubnodes = subnodes.length > 0;

  return (
    <div className="border-ink-200 space-y-2 rounded-2xl border bg-white p-3.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-ink-400 text-[11px] font-semibold uppercase tracking-wide">
          Étape {index + 1}
        </span>
        <span
          className={cn(
            'rounded-full px-2 py-0.5 text-[10px] font-bold uppercase',
            node.kind === 'dispatch'
              ? 'bg-accent-50 text-accent-600'
              : 'bg-primary-50 text-primary-600',
          )}
        >
          {node.kind === 'dispatch' ? 'dispatch' : (node.outil_id ?? 'inference')}
        </span>
        {hasSubnodes && (
          <span className="bg-accent-50 text-accent-600 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase">
            <Layers className="h-3 w-3" /> {TRIGGER_LABEL[node.subnode_trigger]}
          </span>
        )}
      </div>

      <p className="text-ink-900 text-sm font-medium">{node.resultat}</p>

      {parents.length > 0 && (
        <div className="text-ink-500 text-xs">
          <span className="font-semibold">Dépend de : </span>
          {parents
            .map((a) =>
              a.indice_id
                ? `indice « ${indiceById.get(a.indice_id)?.valeur ?? '?'} »`
                : `étape « ${noeudById.get(a.parent_noeud_id ?? '')?.resultat ?? '?'} »`,
            )
            .join(' · ')}
        </div>
      )}

      <ChoixList choix={choix} />

      {hasSubnodes && (
        <div className="pt-1">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            className="border-ink-200 bg-ink-50 text-ink-700 flex min-h-[2.75rem] w-full items-center justify-between gap-2 rounded-xl border px-3 text-sm font-semibold active:scale-[0.99]"
          >
            <span className="flex items-center gap-1.5">
              <Layers className="h-4 w-4" />
              Chemin développé · {subnodes.length} étape{subnodes.length > 1 ? 's' : ''}
            </span>
            <ChevronDown className={cn('h-4 w-4 transition-transform', expanded && 'rotate-180')} />
          </button>

          {expanded && (
            <div className="border-ink-200 mt-2 space-y-2 border-l-2 pl-3">
              {subnodes.map((sub, i) => (
                <SubNodeCard key={sub.id} node={sub} index={i} dag={dag} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SubNodeCard({ node, index, dag }: { node: DagNoeud; index: number; dag: DagPayload }) {
  const choix = dag.choix.filter((c) => c.noeud_id === node.id).sort((a, b) => a.ordre - b.ordre);

  return (
    <div className="border-ink-100 bg-ink-50/40 space-y-1.5 rounded-xl border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-ink-500 text-[11px] font-semibold uppercase tracking-wide">
          Sous-étape {index + 1}
        </span>
        {node.kind === 'inference' && node.outil_id && (
          <span className="bg-primary-50 text-primary-600 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase">
            {node.outil_id}
          </span>
        )}
      </div>
      {node.enonce_qcm && <p className="text-ink-700 text-sm">{node.enonce_qcm}</p>}
      <p className="text-ink-900 text-sm font-medium">{node.resultat}</p>
      <ChoixList choix={choix} />
    </div>
  );
}
